import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import { DASH, formatClock, humanize, isNum, sentimentTone } from "./format";
import type { CallDetail, Turn } from "./types";
import { Unavailable } from "./panels/shared";

const PLAYBACK: Record<string, { text: string; tone: "warning" | "danger" }> = {
  interrupted: { text: "Interrupted", tone: "warning" },
  mid_call_hangup: { text: "Hung up mid-reply", tone: "danger" },
};

export function Transcript({
  turns,
  features,
}: {
  turns: Turn[];
  features?: CallDetail["features"];
}) {
  if (!turns.length) return <p className="text-sm text-ink-3">No transcript is stored for this call.</p>;
  const timed = turns.some((turn) => isNum(turn.start));

  // Turn-level objections
  const objectionsByTurn = new Map<number, string[]>();
  (features?.objection?.result?.objections || []).forEach((obj) => {
    if (obj.turn_index != null) {
      const list = objectionsByTurn.get(obj.turn_index) || [];
      list.push(obj.type);
      objectionsByTurn.set(obj.turn_index, list);
    }
  });

  // Turn-level sentiment
  const sentimentsByTurn = new Map<number, { score: number | null; label: string | null }>();
  (features?.sentiment?.result?.turns || []).forEach((st) => {
    if (st.turn_index != null && (st.label === "negative" || st.label === "positive")) {
      sentimentsByTurn.set(st.turn_index, { score: st.score, label: st.label });
    }
  });

  // QA triggers (dead air & repetition)
  const deadAirByTurn = new Set<number>();
  (features?.qa?.result?.triggers?.dead_air || []).forEach((da) => {
    if (da.turn_index != null) deadAirByTurn.add(da.turn_index);
  });

  const repetitionByTurn = new Set<number>();
  (features?.qa?.result?.triggers?.repetition || []).forEach((r) => {
    if (r.turn_index != null) repetitionByTurn.add(r.turn_index);
  });

  return (
    <div className="space-y-3">
      {!timed ? <Unavailable>This call could not be aligned to its recording, so turns have no times.</Unavailable> : null}
      <ol className="space-y-2.5" aria-label="Transcript">
        {turns.map((turn) => {
          const agent = turn.speaker === "agent";
          const playback = turn.playback ? PLAYBACK[turn.playback] : undefined;
          const turnObjections = objectionsByTurn.get(turn.index) || [];
          const turnSentiment = sentimentsByTurn.get(turn.index);
          const hasDeadAir = deadAirByTurn.has(turn.index);
          const hasRepetition = repetitionByTurn.has(turn.index);

          return (
            <li
              key={turn.index}
              className={cn(
                "rounded-xl border p-3 transition-colors",
                agent
                  ? "border-line bg-canvas/90 dark:bg-canvas/40"
                  : "border-line/70 bg-soft/40 dark:bg-soft/15",
                turnObjections.length > 0 && "border-amber-400/40 bg-amber-500/5",
                turnSentiment?.label === "negative" && "border-rose-400/35 bg-rose-500/5",
              )}
            >
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5 text-xs font-semibold",
                      agent ? "bg-brand/10 text-brand" : "bg-ink-3/15 text-ink-2",
                    )}
                  >
                    {agent ? "Agent" : "Customer"}
                  </span>
                  <span className="font-mono text-[11px] tabular-nums text-ink-3">
                    {formatClock(turn.start)}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {turnObjections.map((type, i) => (
                    <Badge key={i} tone="warning">
                      Objection: {humanize(type)}
                    </Badge>
                  ))}
                  {turnSentiment && (
                    <Badge tone={sentimentTone(turnSentiment.label as any)}>
                      {humanize(turnSentiment.label)}
                    </Badge>
                  )}
                  {hasDeadAir && <Badge tone="neutral">Dead air</Badge>}
                  {hasRepetition && <Badge tone="warning">Repetition</Badge>}
                  {playback ? <Badge tone={playback.tone}>{playback.text}</Badge> : null}
                </div>
              </div>

              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-ink">
                {turn.text || DASH}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
